import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Sparkles, Calendar, MessageCircle, Target, Brain } from 'lucide-react';

interface OnboardingShowcaseProps {
  step: number;
}

export function OnboardingShowcase({ step }: OnboardingShowcaseProps) {
  // Different content based on onboarding step
  const getStepContent = () => {
    switch(step) {
      case 1:
        return {
          headline: "Welcome to AI-Powered Investing",
          description: "Personalized insights for your investment journey",
          features: [
            { icon: Sparkles, title: "AI-Powered Analysis", description: "Get personalized market insights" },
            { icon: TrendingUp, title: "Real-Time Alerts", description: "Never miss important opportunities" }
          ]
        };
      case 2:
        return {
          headline: "Tailored Investment Strategies",
          description: "AI adapts to your investment focus and goals",
          features: [
            { icon: Target, title: "Focused Insights", description: "Analysis tailored to your interests" },
            { icon: Calendar, title: "Economic Calendar", description: "Track events that matter to you" }
          ]
        };
      case 3:
        return {
          headline: "Your AI Investment Assistant",
          description: "Get instant answers to your financial questions",
          features: [
            { icon: MessageCircle, title: "AI Chat Support", description: "Ask questions, get instant answers" },
            { icon: Brain, title: "Smart Recommendations", description: "Personalized investment suggestions" }
          ]
        };
      default:
        return {
          headline: "AI-Powered Market Insights",
          description: "Make smarter investment decisions with AI",
          features: [
            { icon: Sparkles, title: "AI Analysis", description: "Intelligent market insights" },
            { icon: TrendingUp, title: "Real-Time Data", description: "Live market updates" }
          ]
        };
    }
  };

  const content = getStepContent();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
          {content.headline}
        </h1>
        <p className="text-blue-100/80 text-sm max-w-md mx-auto">
          {content.description}
        </p>
      </div>

      {/* Market Overview Chart */}
      <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium text-gray-300">Portfolio Insights</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-emerald-400 font-medium">Live Data</span>
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
          </div>
        </div>
        
        {/* Chart */}
        <div className="relative h-24">
          <svg className="w-full h-full" viewBox="0 0 400 80" preserveAspectRatio="none">
            <motion.path 
              d="M0,60 C50,55 100,50 150,45 C200,40 250,35 300,30 C350,25 400,20 400,20" 
              fill="none" 
              stroke="rgba(34,197,94,0.8)" 
              strokeWidth="2"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, ease: "easeInOut" }}
            />
          </svg>
          
          {/* Data Points */}
          <motion.div 
            className="absolute top-1/3 left-1/4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1, duration: 0.3 }}
          >
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
          </motion.div>
          <motion.div 
            className="absolute top-1/4 right-1/3"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.5, duration: 0.3 }}
          >
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></div>
          </motion.div>
        </div>
      </div>

      {/* Features Grid - matches auth style */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white text-center">What you'll get:</h3>
        
        <div className="grid grid-cols-2 gap-3">
          {content.features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-900/40 border border-gray-700/30 rounded-lg p-3 text-center"
            >
              <feature.icon className="h-5 w-5 text-emerald-400 mx-auto mb-2" />
              <h4 className="text-sm font-medium text-white mb-1">{feature.title}</h4>
              <p className="text-xs text-gray-400">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
